import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import logoSmall from '@/assets/logo-small.webp';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { CartIcon } from '@/components/cart/CartIcon';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

interface AppHeaderProps {
  showNavigation?: boolean;
}

export function AppHeader({ showNavigation = true }: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch user profile to get user type
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const handleSignOut = async () => {
    await signOut();
    toast.success(t('common.logout'));
    navigate('/');
  };

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <header className="bg-background shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center">
              <img 
                src={logoSmall} 
                alt="StatConsult Logo" 
                className="h-10 w-auto"
                width="116"
                height="40"
              />
            </Link>
            {showNavigation && user && (
              <nav className="hidden md:flex space-x-8">
                <Link to="/knowledge-store" className="text-muted-foreground hover:text-foreground">
                  Knowledge Store
                </Link>
                <Link to="/student-portal" className="text-muted-foreground hover:text-foreground">
                  LMS Platform
                </Link>
                {profile?.user_type === 'admin' && (
                  <>
                    <Link to="/admin" className="text-muted-foreground hover:text-foreground">
                      Admin
                    </Link>
                    <Link to="/blog" className="text-muted-foreground hover:text-foreground">
                      Blog
                    </Link>
                  </>
                )}
                <Link to="/about" className="text-muted-foreground hover:text-foreground">
                  About
                </Link>
                <Link to="/contact" className="text-muted-foreground hover:text-foreground">
                  Contact
                </Link>
                <Link to="/faq" className="text-muted-foreground hover:text-foreground">
                  FAQ
                </Link>
              </nav>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {user && (
              <>
                {profile?.user_type === 'client' && <CartIcon />}
                <NotificationBell />
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.profile_picture} />
                  <AvatarFallback>{getInitials(profile?.full_name || '')}</AvatarFallback>
                </Avatar>
                <div className="hidden sm:block">
                  <span className="text-sm font-medium">{profile?.full_name || 'User'}</span>
                  <Badge variant={
                    profile?.user_type === 'statistician' ? 'default' : 
                    profile?.user_type === 'admin' ? 'destructive' :
                    'secondary'
                  } className="ltr:ml-2 rtl:mr-2 text-xs">
                    {profile?.user_type === 'admin' ? 'Administrator' : 
                     profile?.user_type === 'statistician' ? 'Statistician' : 'Client'}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={handleSignOut} aria-label={t('common.logout')}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
            {!user && (
              <div className="flex gap-2">
                <Button asChild variant="outline">
                  <Link to="/auth">{t('navigation.signIn')}</Link>
                </Button>
                <Button asChild>
                  <Link to="/auth">{t('navigation.getStarted')}</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}